import { useMutation, useQueryClient } from '@tanstack/react-query'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'
import { ALL_CAMPAIGNS_TABLE_KEY } from 'constants/tableQueryKeys'
import { CAMPAIGN_STATS_QUERY_KEY } from './campaign_stats_api'
import { campaignSettingsKeys } from './campaign_settings_api'

// Types
export interface CampaignStatusResponse {
  status: string
}

export interface ScheduleCampaignLaunchPayload {
  scheduled_launch_at: string // ISO-8601 UTC datetime
}

// API calls
const launchCampaign = async (
  bisonCampaignId: number
): Promise<CampaignStatusResponse> => {
  return baseAPICall<CampaignStatusResponse>(() =>
    clientInstance.post(`/emailbison/campaigns/${bisonCampaignId}/launch/`)
  )
}

const pauseCampaign = async (
  bisonCampaignId: number
): Promise<CampaignStatusResponse> => {
  return baseAPICall<CampaignStatusResponse>(() =>
    clientInstance.post(`/emailbison/campaigns/${bisonCampaignId}/pause/`)
  )
}

const resumeCampaign = async (
  bisonCampaignId: number
): Promise<CampaignStatusResponse> => {
  return baseAPICall<CampaignStatusResponse>(() =>
    clientInstance.post(`/emailbison/campaigns/${bisonCampaignId}/resume/`)
  )
}

const scheduleCampaignLaunch = async (
  bisonCampaignId: number,
  payload: ScheduleCampaignLaunchPayload
) => {
  return baseAPICall(() =>
    clientInstance.post(
      `/emailbison/campaigns/${bisonCampaignId}/schedule-launch/`,
      payload
    )
  )
}

const cancelScheduledLaunch = async (bisonCampaignId: number) => {
  return baseAPICall(() =>
    clientInstance.post(
      `/emailbison/campaigns/${bisonCampaignId}/cancel-scheduled-launch/`
    )
  )
}

// Shared cache invalidation for all status mutations
function invalidateStatusCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  campaignId?: number
) {
  queryClient.invalidateQueries({ queryKey: [ALL_CAMPAIGNS_TABLE_KEY] })
  queryClient.invalidateQueries({ queryKey: CAMPAIGN_STATS_QUERY_KEY })
  if (campaignId) {
    queryClient.invalidateQueries({
      queryKey: campaignSettingsKeys.byCampaign(campaignId),
    })
  }
}

// Hooks
export const useLaunchCampaign = (campaignId?: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (bisonCampaignId: number) => launchCampaign(bisonCampaignId),
    onSuccess: () => {
      invalidateStatusCaches(queryClient, campaignId)
    },
  })
}

export const usePauseCampaign = (campaignId?: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (bisonCampaignId: number) => pauseCampaign(bisonCampaignId),
    onSuccess: () => {
      invalidateStatusCaches(queryClient, campaignId)
    },
  })
}

export const useResumeCampaign = (campaignId?: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (bisonCampaignId: number) => resumeCampaign(bisonCampaignId),
    onSuccess: () => {
      invalidateStatusCaches(queryClient, campaignId)
    },
  })
}

export const useScheduleCampaignLaunch = (
  bisonCampaignId: number,
  campaignId?: number
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ScheduleCampaignLaunchPayload) =>
      scheduleCampaignLaunch(bisonCampaignId, payload),
    onSuccess: () => {
      invalidateStatusCaches(queryClient, campaignId)
    },
  })
}

export const useCancelScheduledLaunch = (
  bisonCampaignId: number,
  campaignId?: number
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => cancelScheduledLaunch(bisonCampaignId),
    onSuccess: () => {
      invalidateStatusCaches(queryClient, campaignId)
    },
  })
}
